import React, { useState, useEffect } from "react";
import CategorySkeleton from "../Category/CategorySkeleton";
import { useNavigate } from "react-router-dom";

const AllCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        setCategories(data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch categories");
        setLoading(false);
        console.error(err);
      }
    };

    fetchCategories();
  }, []);

  const handleCategoryClick = (categoryId) => {
    navigate(`/allcategories/${categoryId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewMore = () => {
    setShowAll(true);
  };

  if (loading) {
    return <CategorySkeleton />;
  }

  if (error) {
    return <div className="text-center text-red-500 p-4">{error}</div>;
  }

  // For mobile: show 8 categories or all if less than 8, plus always show View More
  const mobileCategoriesToShow = showAll ? categories : categories.slice(0, 8);
  const shouldAlwaysShowViewMore = !showAll; // Always show unless we're showing all

  return (
    <div className="px-4 py-6 w-full">
      {/* Desktop view - unchanged */}
      <div className="hidden sm:flex justify-center gap-6 flex-wrap">
        {categories.map((category) => (
          <div
            key={category._id}
            onClick={() => handleCategoryClick(category._id)}
            className="flex flex-col items-center cursor-pointer group"
          >
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full overflow-hidden mb-3 border-2 border-gray-200 group-hover:border-blue-500 transition-all duration-300">
              <img
                src={category.imgUrl}
                alt={category.name}
                className="absolute h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <h3 className="text-sm sm:text-base font-medium text-gray-800 text-center group-hover:text-blue-600 transition-colors duration-300">
              {category.name}
            </h3>
          </div>
        ))}
      </div>

      {/* Mobile view - shows 8 categories + always shows View More */}
      <div className="grid sm:hidden grid-cols-3 gap-6">
        {mobileCategoriesToShow.map((category) => (
          <div
            key={category._id}
            onClick={() => handleCategoryClick(category._id)}
            className="flex flex-col items-center cursor-pointer group"
          >
            <div className="relative w-full rounded-full overflow-hidden mb-3 aspect-square border-2 border-gray-200 group-hover:border-blue-500 transition-all duration-300">
              <img
                src={category.imgUrl}
                alt={category.name}
                className="absolute h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <h3 className="text-sm font-medium text-gray-800 text-center group-hover:text-blue-600 transition-colors duration-300">
              {category.name}
            </h3>
          </div>
        ))}

        {/* View More button - always shows unless we're showing all categories */}
        {shouldAlwaysShowViewMore && (
          <div
            onClick={handleViewMore}
            className="flex flex-col items-center cursor-pointer group"
          >
            <div className="relative w-full rounded-full overflow-hidden mb-3 aspect-square border-2 border-gray-300 border-dashed group-hover:border-blue-500 transition-all duration-300 flex items-center justify-center bg-gray-50 group-hover:bg-blue-50">
              <div className="text-center">
                <div className="text-2xl text-gray-400 group-hover:text-blue-500 mb-1">
                  +
                </div>
                <div className="text-xs text-gray-500 group-hover:text-blue-600">
                  View
                </div>
                <div className="text-xs text-gray-500 group-hover:text-blue-600">
                  More
                </div>
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 text-center group-hover:text-blue-600 transition-colors duration-300">
              View More
            </h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllCategories;

// import React, { useState, useEffect, useRef } from "react";
// import CategorySkeleton from "../Category/CategorySkeleton";
// import { useNavigate } from "react-router-dom";

// const AllCategories = () => {
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [currentSlide, setCurrentSlide] = useState(0);
//   const navigate = useNavigate();
//   const timerRef = useRef(null);

//   const CATEGORIES_PER_SLIDE = 3;

//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         setLoading(true);
//         const response = await fetch(
//           `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
//         );

//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }

//         const data = await response.json();
//         setCategories(data);
//         setLoading(false);
//       } catch (err) {
//         setError("Failed to fetch categories");
//         setLoading(false);
//         console.error(err);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Calculate total number of slides needed
//   const totalSlides = categories.length
//     ? Math.ceil(categories.length / CATEGORIES_PER_SLIDE)
//     : 0;

//   // Auto-rotate slides every 2 seconds
//   useEffect(() => {
//     if (totalSlides <= 1) return;

//     timerRef.current = setInterval(() => {
//       setCurrentSlide((prevSlide) => (prevSlide + 1) % totalSlides);
//     }, 2000);

//     return () => {
//       if (timerRef.current) {
//         clearInterval(timerRef.current);
//       }
//     };
//   }, [totalSlides]);

//   // Navigate to specific slide
//   const goToSlide = (slideIndex) => {
//     setCurrentSlide(slideIndex);
//     // Reset timer when manually changing slides
//     if (timerRef.current) {
//       clearInterval(timerRef.current);
//       timerRef.current = setInterval(() => {
//         setCurrentSlide((prevSlide) => (prevSlide + 1) % totalSlides);
//       }, 2000);
//     }
//   };

//   if (loading) {
//     return <CategorySkeleton />;
//   }

//   if (error) {
//     return <div className="text-center text-red-500 p-4">{error}</div>;
//   }

//   // Get categories for current slide
//   const startIndex = currentSlide * CATEGORIES_PER_SLIDE;
//   const currentCategories = categories.slice(
//     startIndex,
//     startIndex + CATEGORIES_PER_SLIDE
//   );

//   return (
//     <div className="container mx-auto px-3 sm:px-6 lg:px-8 py-8">
//       {/* Mobile Carousel (visible on small screens) */}
//       <div className="block sm:hidden">
//         <div className="flex justify-between px-2 mb-4">
//           {currentCategories.map((category) => (
//             <div key={category._id} className="flex flex-col items-center">
//               <div
//                 className="rounded-full overflow-hidden w-20 h-20 mb-2 border-2 border-gray-200 hover:border-[#b18f7f] transition-all cursor-pointer"
//                 onClick={() => {
//                   navigate(`/allcategories/${category._id}`);
//                   window.scrollTo({ top: 0, behavior: "smooth" });
//                 }}
//               >
//                 <img
//                   src={category.imgUrl || ""}
//                   alt={category.name}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//               <p className="text-xs text-center font-medium tracking-wide">
//                 {category.name}
//               </p>
//             </div>
//           ))}
//         </div>

//         {/* Navigation Dots */}
//         <div className="flex justify-center mt-4">
//           {Array.from({ length: totalSlides }).map((_, index) => (
//             <button
//               key={index}
//               onClick={() => goToSlide(index)}
//               className={`h-2 w-8 mx-1 rounded-full ${
//                 currentSlide === index ? "bg-red-600" : "bg-gray-300"
//               }`}
//               aria-label={`Go to slide ${index + 1}`}
//             />
//           ))}
//         </div>
//       </div>

//       {/* Regular Grid for larger screens */}
//       <div className="hidden sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-6">
//         {categories.map((category) => (
//           <div key={category._id} className="flex flex-col items-center">
//             <div
//               className="rounded-full overflow-hidden w-32 h-32 md:w-36 md:h-36 mb-2 border-2 border-gray-200 hover:border-[#b18f7f] transition-all cursor-pointer"
//               onClick={() => {
//                 navigate(`/allcategories/${category._id}`);
//                 window.scrollTo({ top: 0, behavior: "smooth" });
//               }}
//             >
//               <img
//                 src={category.imgUrl || ""}
//                 alt={category.name}
//                 className="w-full h-full object-cover"
//               />
//             </div>
//             <p className="text-md text-center font-medium tracking-wide">
//               {category.name}
//             </p>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default AllCategories;
